import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { apiBaseUrl } from '../lib/apiBaseUrl';
import { useAppSession } from '../hooks/useAppSession';
import { LogoMark } from '../components/ui/Logo';
import { log } from '@/lib/logger';
import { capture } from '@/lib/analytics';
import { PLAN_LIMITS, toUserFacingMessage } from '@overlayqa/shared';

type AuthorizeStatus = 'authorizing' | 'success' | 'error';

// The caps the server enforces; a trial resolves to the Team plan.
const SCANS = PLAN_LIMITS.mcpScansPerDay;

const PERMISSIONS = [
  'Create and list projects',
  'Create issues and run scans',
  'View and manage existing issues',
];

export default function McpAuthorizePage() {
  const { getToken } = useAuth();
  const [searchParams] = useSearchParams();
  const port = searchParams.get('port');
  // Funnel attribution: the MCP client appends ?source=mcp so we can tell
  // MCP-originated signups apart from web/extension signups in analytics.
  const source = searchParams.get('source') ?? 'unknown';
  const [status, setStatus] = useState<AuthorizeStatus>('authorizing');
  const [errorMsg, setErrorMsg] = useState('');
  // Account setup (useAppSession -> POST /auth/bootstrap) writes the users row
  // the auth-code route needs. A visitor who signs up from the editor's link
  // lands here while that write is in flight; minting first leaned on the
  // route's 5s server-side retry (routes/mcp/auth.ts) and failed past it. Same
  // class as the 2026-10-16 McpConnectPage incident.
  const { data: session, error: sessionError, isFetching: sessionFetching } = useAppSession();
  const accountReady = !!session;
  const accountError = !accountReady && sessionError && !sessionFetching
    ? toUserFacingMessage(sessionError, 'Authorization failed')
    : '';

  // Funnel start fires on arrival, not after account setup.
  useEffect(() => {
    capture('mcp_authorize_started', { source });
  }, [source]);

  useEffect(() => {
    log.info('[McpAuthorizePage] effect fired', { port, source, accountReady });

    if (!port) {
      log.error('[McpAuthorizePage] missing port query parameter');
      setStatus('error');
      setErrorMsg(
        'Missing port parameter. Please restart the MCP authentication flow from your editor.'
      );
      return;
    }

    const parsedPort = Number.parseInt(port, 10);
    if (!Number.isFinite(parsedPort) || parsedPort <= 0 || parsedPort > 65535) {
      log.error('[McpAuthorizePage] invalid port query parameter', { port });
      setStatus('error');
      setErrorMsg('Invalid port parameter. Please restart the MCP authentication flow from your editor.');
      return;
    }

    if (accountError) {
      setStatus('error');
      setErrorMsg(accountError);
      return;
    }
    if (!accountReady) return;

    let cancelled = false;

    async function authorize() {
      try {
        const token = await getToken();
        if (!token) {
          throw new Error('Failed to get Clerk session token');
        }

        const apiBase = apiBaseUrl();
        log.info('[McpAuthorizePage] POST /api/mcp/auth-code', { apiBase, port: parsedPort });

        const res = await fetch(`${apiBase}/api/mcp/auth-code`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ port: parsedPort, source }),
        });

        if (!res.ok) {
          const errPayload = (await res.json().catch(() => null)) as
            | { error?: { code?: string; message?: string } }
            | null;
          const message =
            errPayload?.error?.message || `Authorization failed (HTTP ${res.status})`;
          throw new Error(message);
        }

        const { redirectUrl } = (await res.json()) as { redirectUrl: string };
        if (!redirectUrl || typeof redirectUrl !== 'string') {
          throw new Error('Server did not return a redirect URL');
        }

        if (cancelled) return;
        log.info('[McpAuthorizePage] auth-code success, redirecting', { redirectUrl });
        capture('mcp_connected', { source });
        setStatus('success');

        window.location.href = redirectUrl;
      } catch (err) {
        if (cancelled) return;
        log.error('[McpAuthorizePage] authorization failed', {
          message: err instanceof Error ? err.message : String(err),
        });
        setStatus('error');
        setErrorMsg(toUserFacingMessage(err, 'Authorization failed'));
      }
    }

    void authorize();

    return () => {
      cancelled = true;
    };
  }, [port, source, getToken, accountReady, accountError]);

  return (
    <div className="auth-shell flex min-h-screen items-center justify-center px-4">
      <div className="auth-shell__panel">
        <div className="space-y-6 text-center animate-fade-in">
          {/* Logo */}
          <div className="flex justify-center">
            <LogoMark className="w-10 h-10" />
          </div>

          {status === 'authorizing' && (
            <>
              <div className="space-y-1">
                <h2 className="hero-title">
                  Connect to OverlayQA
                </h2>
                <p className="text-sm text-gray-500">
                  Your editor is requesting access to your account.
                </p>
              </div>

              {/* Spinner */}
              <div className="flex justify-center py-2">
                <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
              </div>

              {/* Permissions scope */}
              <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-left">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-400 mb-2">
                  This will allow your editor to:
                </p>
                <ul className="space-y-2">
                  {PERMISSIONS.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm text-gray-600">
                      <svg className="mt-1 h-4 w-4 shrink-0 text-primary-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {p}
                    </li>
                  ))}
                </ul>
                <p className="mt-3 border-t border-gray-100 pt-3 text-xs text-gray-400">
                  {`Daily scans: Starter ${SCANS.starter}, Team and the 14-day trial ${SCANS.team}, Pro ${SCANS.pro === -1 ? 'unlimited' : SCANS.pro}. Without a plan, ${SCANS.none}.`}
                </p>
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="space-y-1">
                <h2 className="hero-title">
                  Connected
                </h2>
                <p className="text-sm text-gray-500">
                  Redirecting back to your editor...
                </p>
              </div>

              {/* Checkmark */}
              <div className="flex justify-center py-2">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-50">
                  <svg className="h-6 w-6 text-green-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>

              <p className="text-xs text-gray-400">
                You can close this tab once your editor confirms the connection.
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="space-y-1">
                <h2 className="hero-title">
                  Connection failed
                </h2>
                <p className="text-sm text-gray-500">
                  Something went wrong while connecting your editor.
                </p>
              </div>

              <div className="auth-card__form-error text-left" role="alert">
                {errorMsg}
              </div>

              <p className="text-sm text-gray-500">
                Restart the MCP server in your editor to try again.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
